<template>
  <div>
    <NavBar />
    <div class="container">
      <div class="row justify-content-center">
        <div class="col col-md-8 col-lg-6">
          <h1 class="text-center">Create Organization</h1>
          <ErrorSummary :error="error" />
          <div class="card">
            <h5 class="card-header">Profile</h5>
            <div class="card-body">
              <form ref="form" class="needs-validation" :class="formWasValidated ? 'was-validated' : ''" novalidate @submit.prevent>
                <div class="form-group">
                  <label for="legalName">Legal Name</label>
                  <input
                    v-model="legalName"
                    type="text"
                    class="form-control"
                    id="legalName"
                    placeholder="Legal Name"
                    required
                    :disabled="loadingMessage"
                    @keyup.enter="submit"
                  />
                  <div class="invalid-feedback">
                    Please provide a legal name.
                  </div>
                </div>

                <div class="form-group">
                  <label for="displayName">Display Name</label>
                  <input
                    v-model="displayName"
                    type="text"
                    class="form-control"
                    id="displayName"
                    placeholder="Display Name"
                    required
                    :disabled="loadingMessage"
                    @keyup.enter="submit"
                  />
                  <div class="invalid-feedback">
                    Please provide a display name.
                  </div>
                </div>
                <div class="form-group">
                  <div class="custom-control custom-switch">
                    <input
                      type="checkbox"
                      class="custom-control-input"
                      id="collectDemographicData"
                      :disabled="loadingMessage"
                      v-model="collectDemographicData"
                    />
                    <label class="custom-control-label" for="collectDemographicData">Collect Demographic Data</label>
                  </div>
                </div>
                <div class="form-group">
                  <div class="custom-control custom-switch">
                    <input type="checkbox" class="custom-control-input" id="extendedTimeDefault" :disabled="loadingMessage" v-model="extendedTimeDefault" />
                    <label class="custom-control-label" for="extendedTimeDefault">Interview Extended Time Default</label>
                  </div>
                </div>
                <div class="form-group">
                  <div class="custom-control custom-switch">
                    <input
                      type="checkbox"
                      class="custom-control-input"
                      id="sendInterviewFinishedNotificationEmail"
                      :disabled="loadingMessage"
                      v-model="sendInterviewFinishedNotificationEmail"
                    />
                    <label class="custom-control-label" for="sendInterviewFinishedNotificationEmail">Send Interview Finished Notification Email</label>
                  </div>
                </div>
                <div class="form-group">
                  <div class="custom-control custom-switch">
                    <input
                      type="checkbox"
                      class="custom-control-input"
                      id="showInvestigationRequestForContact"
                      :disabled="loadingMessage"
                      v-model="showInvestigationRequestForContact"
                    />
                    <label class="custom-control-label" for="showInvestigationRequestForContact">
                      Show Request for Contact prompt at end of Investigation interview
                    </label>
                  </div>
                </div>
                <div class="form-group">
                  <div class="custom-control custom-switch">
                    <input
                      type="checkbox"
                      class="custom-control-input"
                      id="showInvestigationTipReportScreen"
                      :disabled="loadingMessage"
                      v-model="showInvestigationTipReportScreen"
                    />
                    <label class="custom-control-label" for="showInvestigationTipReportScreen">
                      Show Tip Report Screen at end of Investigation interview
                    </label>
                  </div>
                </div>
                <div class="form-group" v-if="showInvestigationTipReportScreen">
                  <label for="tipReportScreenUrl">Tip Report Screen URL</label>
                  <input
                    v-model="tipReportScreenUrl"
                    type="url"
                    class="form-control"
                    id="tipReportScreenUrl"
                    placeholder="https://example.com/"
                    required
                    :disabled="loadingMessage"
                    @keyup.enter="submit"
                  />
                  <div class="invalid-feedback">
                    Please provide a valid URL.
                  </div>
                </div>
                <div class="form-group">
                  <label for="interviewInviteExpiration">Number of Days for Interview Invite Expiration</label>
                  <select
                    v-model.number="interviewInviteExpiration"
                    :disabled="loadingMessage"
                    id="interviewInviteExpiration"
                    class="form-control"
                    style="width: 70px;"
                  >
                    <option>4</option>
                    <option>5</option>
                    <option>6</option>
                    <option>7</option>
                    <option>8</option>
                    <option>9</option>
                    <option>10</option>
                    <option>11</option>
                    <option>12</option>
                    <option>13</option>
                    <option>14</option>
                  </select>
                </div>
              </form>
            </div>
            <div class="card-footer">
              <button type="button" class="btn btn-primary btn-block" @click="submit" :disabled="loadingMessage" @keyup.enter="submit">
                <span v-if="loadingMessage" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                {{ loadingMessage ? loadingMessage : "Create" }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import NavBar from "@/views/reports/NavBar.vue";
import ErrorSummary from "@/components/ErrorSummary.vue";

export default {
  components: {
    NavBar,
    ErrorSummary,
  },
  data() {
    return {
      loadingMessage: null,
      formWasValidated: false,
      error: null,

      legalName: null,
      displayName: null,
      collectDemographicData: false,
      extendedTimeDefault: false,
      sendInterviewFinishedNotificationEmail: false,
      showInvestigationRequestForContact: false,
      showInvestigationTipReportScreen: false,
      tipReportScreenUrl: null,
      interviewInviteExpiration: 8,
    };
  },
  methods: {
    reset() {
      this.loadingMessage = null;
      this.formWasValidated = false;
      this.error = null;
    },
    submit() {
      const payload = {
        legalName: this.legalName,
        displayName: this.displayName,
        collectDemographicData: this.collectDemographicData,
        extendedTimeDefault: this.extendedTimeDefault,
        sendInterviewFinishedNotificationEmail: this.sendInterviewFinishedNotificationEmail,
        showInvestigationRequestForContact: this.showInvestigationRequestForContact,
        showInvestigationTipReportScreen: this.showInvestigationTipReportScreen,
        tipReportScreenUrl: this.tipReportScreenUrl,
        interviewInviteExpiration: this.interviewInviteExpiration,
      };

      // reset
      this.reset();

      // validations
      if (!this.$refs.form.checkValidity()) {
        this.formWasValidated = true;
        return;
      }

      // api
      this.loadingMessage = "Saving...";
      this.$axios
        .post("/v1/organizations", payload)
        .then(res => this.$router.push(`/organizations/${res.data.id}`))
        .catch(err => (this.error = err))
        .finally(() => (this.loadingMessage = null));
    },
  },
};
</script>
